import React, { lazy, Suspense } from 'react'
import { Helmet } from 'react-helmet'
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom'
import { Behavior, Indicators } from 'ui'
import {
  ConfirmAccountView,
  CreateAccountView,
  ForgotPasswordView,
  InitialSignUpView,
  LoginView,
  ResetPasswordView,
  TokenView,
} from '../views'
import { ConditionalRoute } from './ConditionalRoute'
import { usePageViews } from './usePageViews'
import { SessionRedirect } from './SessionRedirect'
import { useAuthStatus } from './useAuthStatus'

/**
 * The admin portion of the application is lazy loaded
 * to prevent long load times.
 */
const AdminView = lazy(() => import('./AdminView'))

/**
 * The internal application portion of the app is
 * lazy loaded to prevent long load times.
 */
const AppNavigator = lazy(() => import('./AppNavigator'))

const DefaultSwitch = () => {
  usePageViews()
  const { admin, authenticated } = useAuthStatus()
  const loggedIn = () => authenticated
  const loggedOut = () => !loggedIn()
  const canViewAdmin = () => loggedIn() && admin
  const canNotViewAdmin = () => !canViewAdmin()
  return (
    <Switch>
      <ConditionalRoute
        path="/"
        component={InitialSignUpView}
        redirectIf={loggedIn}
        exact={true}
        redirectPath={'/session'}
      />
      <Route
        path="/confirm/:role"
        component={ConfirmAccountView}
        exact={true}
      />
      <Route path="/session" component={SessionRedirect} exact={true} />
      <Route path="/signup" component={CreateAccountView} />
      <ConditionalRoute
        path="/login/forgot"
        component={ForgotPasswordView}
        redirectIf={loggedIn}
        exact={true}
        redirectPath={'/session'}
      />
      <ConditionalRoute
        path="/login/reset"
        component={ResetPasswordView}
        redirectIf={loggedIn}
        exact={true}
        redirectPath={'/session'}
      />
      <ConditionalRoute
        path="/login"
        component={LoginView}
        redirectIf={loggedIn}
        exact={true}
        redirectPath={'/session'}
      />
      <ConditionalRoute
        path="/admin"
        component={AdminView}
        redirectIf={canNotViewAdmin}
        redirectPath={'/app'}
      />
      <ConditionalRoute
        path="/app"
        component={AppNavigator}
        redirectIf={loggedOut}
        redirectPath={'/login'}
      />
      <Route path="/token/:token" component={TokenView} />
      <Redirect to="/app" />
    </Switch>
  )
}

export const RootRouter: React.FunctionComponent = () => {
  return (
    <Router>
      <Behavior.ScrollToTop />
      <Helmet>
        <meta charSet="utf-8" />
        <title>MCSS PRO Connect</title>
      </Helmet>
      <Suspense fallback={<Indicators.FullScreen />}>
        <DefaultSwitch />
      </Suspense>
    </Router>
  )
}
